import PropTypes from "prop-types";
import * as React from "react";

import { detectIE } from "../../../../Engine/utils";
import { pullContext } from "../../../TypingUtils";
import {
    DataPasteContext,
    RegisterTableDataPasteFunction,
    TableDataPasteContext,
} from "../../MultiLine/TableDataPasteContext";
import { Colgroup } from "../Colgroup";
import { areArraysNotEqual } from "../../../../../../Common/ArrayUtils";

import { StickyTableBodyRowProps } from "./StickyTableBodyRow";
import { TableRoot } from "./StickyTableData.styles";
import { StickyTableBodyRowContainer } from "./StickyTableBodyRowContainer";

export interface DataProps {
    children: React.ReactNode;
    className?: string;
    columnWidth: number[];
    heightRows: number[];
    width: number;
}

interface DataContext {
    setDataBody: (el: null | HTMLElement) => void;
}

export class StickyTableData extends React.Component<DataProps> {
    public static contextTypes = {
        setDataBody: PropTypes.func,
        registerRowDataPasteFunc: PropTypes.func,
        processTableDataPaste: PropTypes.func,
        fillMultiline: PropTypes.func,
    };

    public shouldComponentUpdate(nextProps: DataProps): boolean {
        return (
            nextProps.width !== this.props.width ||
            nextProps.heightRows !== this.props.heightRows ||
            areArraysNotEqual(nextProps.columnWidth, this.props.columnWidth)
        );
    }

    public render(): React.ReactNode {
        const rows = React.Children.map(this.props.children ?? [], (child, i) => {
            if (!React.isValidElement(child)) {
                return child;
            }

            const tableDataPasteContext = pullContext<TableDataPasteContext>(this);

            if (child.type === "tr" || child.type === StickyTableBodyRowContainer) {
                const rowProp: StickyTableBodyRowProps = {
                    registerRowPasteFunc: (func: RegisterTableDataPasteFunction) =>
                        tableDataPasteContext.registerRowDataPasteFunc(func, i),
                    fillMultiline: tableDataPasteContext.fillMultiline,
                    processTableDataPaste: (
                        clipboardContent: DataTransfer,
                        context: DataPasteContext,
                        columnStartIndex?: number
                    ) => tableDataPasteContext.processTableDataPaste(clipboardContent, context, i, columnStartIndex),
                };
                if (this.props.heightRows[i]) {
                    rowProp.style = { height: `${this.props.heightRows[i]}px` };
                }
                return React.cloneElement(child, rowProp);
            }

            const props = {
                height: this.props.heightRows[i],
                columnWidth: this.props.columnWidth,
                registerMultilinePasteFunc: (func: RegisterTableDataPasteFunction) =>
                    tableDataPasteContext.registerRowDataPasteFunc(func, i),
                fillMultiline: tableDataPasteContext.fillMultiline,
                processTableDataPaste: (
                    clipboardContent: DataTransfer,
                    context: DataPasteContext,
                    columnStartIndex?: number
                ) => tableDataPasteContext.processTableDataPaste(clipboardContent, context, i, columnStartIndex),
            };
            return React.cloneElement(child, props);
        });

        // IE11 When data columns less than header columns
        const style = detectIE() ? { width: this.props.columnWidth.reduce((a, b) => a + b) } : undefined;

        return (
            <TableRoot className={this.props.className} style={style}>
                <Colgroup columnWidth={this.props.columnWidth} />
                <tbody ref={this.refData}>{rows}</tbody>
            </TableRoot>
        );
    }

    private readonly refData = (el: null | HTMLTableSectionElement) => {
        this.getContext().setDataBody(el);
    };

    private getContext(): DataContext {
        return pullContext<DataContext>(this);
    }
}
